import React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import cn from './cn';

/**
 * Radix Tooltip, in the shape the CMS call sites want.
 *
 * The app had two tooltip systems: MUI's `<Tooltip title=…>` wrapper and Bootstrap's
 * `<OverlayTrigger overlay={<Tooltip/>}>`. Both take the trigger as a child and the
 * content as a prop, so `Tooltip` below keeps that shape rather than exposing Radix's
 * Root/Trigger/Content split. A call site reads the same as it did; only the
 * implementation and the styling changed.
 *
 * `Provider` has to wrap the app once. It is rendered by `store/rootComponents`, next to
 * the MUI theme, so a tooltip works anywhere without each screen remembering.
 */
export const TooltipProvider = TooltipPrimitive.Provider;

export const TooltipContent = React.forwardRef(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'tw-z-dialog tw-max-w-xs tw-rounded-control tw-bg-ink tw-px-2 tw-py-1',
      'tw-text-caption tw-text-white tw-font-sans tw-animate-cms-fade-in',
      className
    )}
    {...props}
  />
));
TooltipContent.displayName = 'TooltipContent';

/**
 * `title` is the content, matching MUI's prop name so the 7 MUI call sites read the same.
 * An empty `title` renders the child alone, which is what both old systems did.
 */
const Tooltip = ({ title, children, side = 'top', delayDuration = 200, className, ...rest }) => {
  if (!title) return children;
  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipContent side={side} className={className} {...rest}>
          {title}
          <TooltipPrimitive.Arrow className="tw-fill-ink" />
        </TooltipContent>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};

export default Tooltip;
